import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Trash2, Mail, ShieldCheck, UserRound } from "lucide-react";

export const inputCls = "w-full bg-black border border-white/15 rounded px-3 py-3 text-sm text-[#F5F2EB] placeholder-[#52525B] focus:outline-none focus:border-[#D4AF37]";
export const labelCls = "block text-[10px] uppercase tracking-[0.22em] text-[#A1A1AA] mb-1.5";

export function PhotoSection({ user, refresh }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("photo", f);
      await api.post("/users/me/photo", fd);
      await refresh?.();
      toast.success("Photo updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload photo");
    } finally { setBusy(false); e.target.value = ""; }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/users/me/photo");
      await refresh?.();
      toast.success("Photo removed");
    } catch { toast.error("Could not remove photo"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className={labelCls}>Profile photo</div>
      <div className="flex items-center gap-4">
        {user?.picture ? (
          <img src={user.picture} alt="" className="w-16 h-16 rounded-full object-cover border border-[#D4AF37]/40" data-testid="settings-avatar"/>
        ) : (
          <div className="w-16 h-16 rounded-full bg-white/5 border border-white/15 flex items-center justify-center" data-testid="settings-avatar-placeholder">
            <UserRound size={24} className="text-[#71717a]"/>
          </div>
        )}
        <div className="flex gap-2">
          <button data-testid="photo-upload-btn" disabled={busy} onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1.5 border border-white/20 px-4 py-2 text-[10px] uppercase tracking-[0.16em] text-[#F5F2EB] hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-50">
            <Camera size={13}/> {user?.picture ? "Change" : "Add photo"}
          </button>
          {user?.picture && (
            <button data-testid="photo-remove-btn" disabled={busy} onClick={remove}
              className="flex items-center gap-1.5 border border-red-500/40 px-4 py-2 text-[10px] uppercase tracking-[0.16em] text-red-400 hover:bg-red-500/10 disabled:opacity-50">
              <Trash2 size={13}/> Remove
            </button>
          )}
        </div>
        <input ref={fileRef} data-testid="photo-file-input" type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={upload}/>
      </div>
    </div>
  );
}

export function ContactSection({ user, refresh }) {
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [busy, setBusy] = useState(false);
  const dirty = name !== (user?.name || "") || phone !== (user?.phone || "");

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/users/me", { name, phone });
      await refresh?.();
      toast.success("Contact details updated — upcoming trips use the new info");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div>
        <label className={labelCls}>Full name</label>
        <input data-testid="settings-name-input" className={inputCls} value={name} onChange={e => setName(e.target.value)} placeholder="Your name"/>
      </div>
      <div>
        <label className={labelCls}>Mobile phone</label>
        <input data-testid="settings-phone-input" type="tel" className={inputCls} value={phone} onChange={e => setPhone(e.target.value)} placeholder="(414) 555-0123"/>
      </div>
      <button data-testid="settings-save-contact-btn" disabled={busy || !dirty} onClick={save}
        className="btn-gold !py-2.5 !px-6 text-[11px] disabled:opacity-40">
        {busy ? "Saving…" : "Save changes"}
      </button>
    </div>
  );
}

export function EmailSection({ user, refresh }) {
  const [newEmail, setNewEmail] = useState("");
  const [pending, setPending] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const requestChange = async () => {
    setBusy(true);
    try {
      const r = await api.post("/users/me/email/request", { new_email: newEmail.trim() });
      setPending(r.data);
      toast.success(r.data.message);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send the code");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await api.post("/users/me/email/verify", { pending_token: pending.pending_token, code: code.trim() });
      await refresh?.();
      setPending(null); setNewEmail(""); setCode("");
      toast.success("Email updated — corporate membership and referral code moved with it");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not verify the code");
    } finally { setBusy(false); }
  };

  return (
    <div className="border-t border-white/10 pt-6">
      <div className={labelCls}>Email address</div>
      <div className="flex items-center gap-2 text-sm text-[#F5F2EB] mb-3" data-testid="settings-current-email">
        <Mail size={14} className="text-[#D4AF37]"/> {user?.email}
      </div>
      {!pending ? (
        <div className="flex gap-2">
          <input data-testid="settings-new-email-input" type="email" className={inputCls} value={newEmail}
            onChange={e => setNewEmail(e.target.value)} placeholder="new@email.com"/>
          <button data-testid="settings-email-request-btn" disabled={busy || !newEmail.trim()} onClick={requestChange}
            className="shrink-0 border border-white/20 px-4 text-[10px] uppercase tracking-[0.14em] text-[#F5F2EB] hover:border-[#D4AF37] hover:text-[#D4AF37] disabled:opacity-40">
            {busy ? "Sending…" : "Change"}
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-[#A1A1AA] flex items-center gap-1.5"><ShieldCheck size={13} className="text-[#D4AF37]"/> Enter the 6-digit code we emailed to the new address.</p>
          <div className="flex gap-2">
            <input data-testid="settings-email-code-input" className={`${inputCls} text-center tracking-[0.4em]`} value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="••••••" inputMode="numeric"/>
            <button data-testid="settings-email-verify-btn" disabled={busy || code.length !== 6} onClick={verify}
              className="shrink-0 btn-gold !px-5 text-[10px] disabled:opacity-40">Confirm</button>
          </div>
          <button data-testid="settings-email-cancel-btn" onClick={() => { setPending(null); setCode(""); }}
            className="text-[10px] text-[#71717a] uppercase tracking-[0.14em] hover:text-white">Cancel</button>
        </div>
      )}
    </div>
  );
}
